import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import IconButton from '@atlaskit/button/icon/button';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Popup } from '@atlaskit/popup/popup';
import Toggle from '@atlaskit/toggle/toggle';
import { auth } from '@atlaskit/outbound-auth-flow-client';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import {
	ALL_EXTERNAL_PRODUCT_CONNECTIONS,
	AUTH_OPTIONS,
	EXTERNAL_PRODUCT_CONNECTION,
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

// Loom is excluded — it needs no third-party OAuth to be filterable.
const AUTH_REQUIRED_SOURCES = new Set<ExternalProductConnection>(AUTH_OPTIONS);

const i18n = defineMessages({
	filterButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.filter-button-label',
		defaultMessage: 'Filter suggestions by source',
		description:
			'Accessible label for the settings button that opens the suggestions source filter popup',
	},
	showOnlyFrom: {
		id: 'space-curation.review-content-suggestions-modal.filter-show-only-from',
		defaultMessage: 'Show only suggestions from',
		description: 'Heading at the top of the suggestions source filter popup',
	},
	googleDrive: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-google-drive',
		defaultMessage: 'Google Drive',
		description: 'Display name for the Google Drive source in the suggestions filter popup',
	},
	loom: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-loom',
		defaultMessage: 'Loom',
		description: 'Display name for the Loom source in the suggestions filter popup',
	},
	microsoft: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-microsoft',
		defaultMessage: 'Microsoft',
		description: 'Display name for the Microsoft source in the suggestions filter popup',
	},
	connect: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-connect',
		defaultMessage: 'Connect',
		description:
			'Button shown next to a source the user has not connected yet; clicking it starts the OAuth connect flow so the source can be filtered on',
	},
});

const styles = cssMap({
	popupContent: {
		padding: token('space.075'),
		width: '260px',
	},
	sourceRow: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	heading: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	sourceName: {
		flexGrow: 1,
		minWidth: '0%',
	},
	iconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		flexShrink: 0,
	},
});

type FilterSource = {
	key: ExternalProductConnection;
	iconUrl: string;
	message: (typeof i18n)[keyof typeof i18n];
};

const FILTER_SOURCES: FilterSource[] = [
	{
		key: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
		message: i18n.googleDrive,
	},
	{
		key: EXTERNAL_PRODUCT_CONNECTION.LOOM,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
		message: i18n.loom,
	},
	{
		key: EXTERNAL_PRODUCT_CONNECTION.MICROSOFT,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT],
		message: i18n.microsoft,
	},
];

export type EnabledSources = Record<ExternalProductConnection, boolean>;

type SuggestionsFilterPopupProps = {
	onClose: (sources: ExternalProductConnection[]) => void;
};

export const SuggestionsFilterPopup = ({
	onClose,
}: SuggestionsFilterPopupProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [enabledSources, setEnabledSources] = useState<EnabledSources>(
		() =>
			Object.fromEntries(
				ALL_EXTERNAL_PRODUCT_CONNECTIONS.map((key) => [key, true]),
			) as EnabledSources,
	);

	const { cloudId } = useSessionData();
	const {
		authenticatedServices,
		unauthenticatedServices,
		refetch,
		markOptimisticallyAuthenticated,
	} = useFetch3PAuthentications({
		products: [...AUTH_REQUIRED_SOURCES],
		cloudId,
	});

	const isConnected = useCallback(
		(key: ExternalProductConnection): boolean =>
			!AUTH_REQUIRED_SOURCES.has(key) || Boolean(authenticatedServices[key]?.isAuthenticated),
		[authenticatedServices],
	);

	const needsConnect = useCallback(
		(key: ExternalProductConnection): boolean =>
			AUTH_REQUIRED_SOURCES.has(key) && Boolean(unauthenticatedServices[key]),
		[unauthenticatedServices],
	);
	const isChecked = useCallback(
		(key: ExternalProductConnection): boolean => isConnected(key) && Boolean(enabledSources[key]),
		[isConnected, enabledSources],
	);

	const closePopup = useCallback(() => {
		setIsOpen(false);
		const connected = ALL_EXTERNAL_PRODUCT_CONNECTIONS.filter((key) => isConnected(key));
		const enabledConnected = connected.filter((key) => enabledSources[key]);
		onClose(
			enabledConnected.length === connected.length
				? [...ALL_EXTERNAL_PRODUCT_CONNECTIONS]
				: enabledConnected,
		);
	}, [onClose, isConnected, enabledSources]);

	const handleToggle = (key: ExternalProductConnection) => {
		setEnabledSources((prev) => ({ ...prev, [key]: !prev[key] }));
	};

	const handleConnect = async (key: ExternalProductConnection) => {
		const authUrl = unauthenticatedServices[key]?.outboundAuthUrl;
		if (!authUrl) {
			return;
		}
		try {
			await auth(authUrl);
			markOptimisticallyAuthenticated(key);
			setEnabledSources((prev) => ({ ...prev, [key]: true }));
			await refetch();
		} catch {
			// Cancelled or failed — leave the source unconnected.
		}
	};

	const enabledCount = useMemo(
		() => FILTER_SOURCES.reduce((count, source) => (isChecked(source.key) ? count + 1 : count), 0),
		[isChecked],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={closePopup}
			placement="bottom-end"
			zIndex={800}
			content={() => (
				<Box xcss={styles.popupContent} testId="suggestions-filter-popup-content">
					<Stack space="space.0">
						<Box xcss={styles.heading}>
							<Text size="small" weight="bold" color="color.text.subtlest">
								{formatMessage(i18n.showOnlyFrom)}
							</Text>
						</Box>
						<Stack space="space.0">
							{FILTER_SOURCES.map((source) => {
								const name = formatMessage(source.message);
								const checked = isChecked(source.key);
								const showConnect = needsConnect(source.key);
								const configIcon = (
									authenticatedServices[source.key] ?? unauthenticatedServices[source.key]
								)?.icon;
								return (
									<Flex
										key={source.key}
										alignItems="center"
										justifyContent="space-between"
										gap="space.100"
										xcss={styles.sourceRow}
									>
										<Inline alignBlock="center" space="space.100">
											<Box xcss={styles.iconWrapper}>
												{configIcon ? (
													configIcon({ size: 'small' })
												) : (
													<img
														src={source.iconUrl}
														alt=""
														aria-hidden="true"
														width="16"
														height="16"
													/>
												)}
											</Box>
											<Box xcss={styles.sourceName}>
												<Text size="medium">{name}</Text>
											</Box>
										</Inline>
										{showConnect ? (
											<Button
												appearance="subtle"
												spacing="compact"
												onClick={() => void handleConnect(source.key)}
												testId={`suggestions-filter-connect-${source.key}`}
											>
												{formatMessage(i18n.connect)}
											</Button>
										) : (
											<Toggle
												label={name}
												isChecked={checked}
												isDisabled={checked && enabledCount === 1}
												onChange={() => handleToggle(source.key)}
												testId={`suggestions-filter-toggle-${source.key}`}
											/>
										)}
									</Flex>
								);
							})}
						</Stack>
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={CustomizeIcon}
					label={formatMessage(i18n.filterButtonLabel)}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
					onClick={() => (isOpen ? closePopup() : setIsOpen(true))}
					testId="suggestions-filter-button"
				/>
			)}
		/>
	);
};
